import type { SupabaseClient, PostgrestError } from '@supabase/supabase-js';
import type { StorageError } from '@supabase/storage-js';
import { getSupabaseClient } from './supabase';
import { recordRealtimeEvent } from '../lib/realtimeTelemetry';

type ClientOption = { supabase?: SupabaseClient };

export type ChatProfile = {
  id: string;
  full_name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  headshot_url?: string | null;
};

export type ChatMessageRow = {
  id: string;
  event_id: string;
  conversation_id?: string | null;
  author_profile_id: string;
  author_display_name?: string | null;
  body: string;
  attachments: any[];
  mentions: any[];
  created_at: string;
  updated_at: string;
  author?: ChatProfile | null;
};

export type ChatMentionStatusRow = {
  id: string;
  message_id: string;
  mentioned_profile_id: string;
  read_at: string | null;
  dismissed_at: string | null;
  profile?: ChatProfile | null;
};

export type ChatReadReceiptRow = {
  id: string;
  message_id: string;
  reader_profile_id: string;
  read_at: string;
  reader?: ChatProfile | null;
};

export type ChatMentionRow = {
  id: string;
  message_id: string;
  event_id: string;
  created_at: string;
  read_at?: string | null;
  dismissed_at?: string | null;
  message: {
    id: string;
    body: string;
    attachments: any[];
    mentions: Array<{ profile_id: string; display_name: string }>;
    created_at: string;
    author_profile_id: string;
    author?: ChatProfile | null;
    author_display_name?: string | null;
  } | null;
  event: {
    id: string;
    name?: string | null;
    event_code?: string | null;
  } | null;
};

const normaliseProfile = (row: any): ChatProfile | null => {
  if (!row || typeof row !== 'object') return null;
  const id = row.id ? String(row.id) : '';
  if (!id) return null;
  return {
    id,
    full_name: row.full_name ?? null,
    first_name: row.first_name ?? null,
    last_name: row.last_name ?? null,
    email: row.email ?? null,
    headshot_url: row.headshot_url ?? null,
  };
};

const unwrapProfile = (value: any): ChatProfile | null => {
  if (!value) return null;
  const source = Array.isArray(value) ? value[0] : value;
  return normaliseProfile(source);
};

const normaliseMentionRow = (row: any): ChatMentionRow => {
  const message = Array.isArray(row?.message) ? row.message[0] ?? null : row?.message ?? null;
  const event = Array.isArray(row?.event) ? row.event[0] ?? null : row?.event ?? null;
  return {
    id: String(row.id),
    message_id: String(row.message_id),
    event_id: String(row.event_id),
    created_at: String(row.created_at),
    read_at: row.read_at ?? null,
    dismissed_at: row.dismissed_at ?? null,
    message,
    event,
  };
};

export async function fetchChatMessages(
  eventId: string,
  options?: ClientOption,
): Promise<{ data: ChatMessageRow[]; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase
    .from('quote_chat_messages')
    .select(
      `id,event_id,conversation_id,author_profile_id,author_display_name,body,attachments,mentions,created_at,updated_at,
       author:author_profile_id(id,full_name,first_name,last_name,email,headshot_url)`,
    )
    .eq('event_id', eventId)
    .order('created_at', { ascending: true })
    .limit(200);
  const rows = Array.isArray(data)
    ? (data as any[]).map((row) => ({
        ...(row as any),
        author: unwrapProfile((row as any).author),
      }))
    : [];
  return { data: rows as ChatMessageRow[], error: error ?? null };
}

export function subscribeChatMessages(params: {
  eventId: string;
  supabase?: SupabaseClient;
  onChange: (payload: any) => void;
}): () => void {
  const supabase = params.supabase ?? getSupabaseClient();
  const channel = supabase
    .channel(`quote-chat:${params.eventId}`)
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'quote_chat_messages', filter: `event_id=eq.${params.eventId}` },
      (payload) => {
        recordRealtimeEvent({ source: 'order-chat-messages', table: 'quote_chat_messages', eventType: payload.eventType });
        params.onChange(payload);
      },
    )
    .subscribe();
  return () => {
    try {
      supabase.removeChannel(channel);
    } catch {
      /* noop */
    }
  };
}

export async function fetchChatMentionStatuses(
  eventId: string,
  options?: ClientOption,
): Promise<{ data: ChatMentionStatusRow[]; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase
    .from('quote_chat_mentions')
    .select('id,message_id,mentioned_profile_id,read_at,dismissed_at,profile:mentioned_profile_id(id,full_name,first_name,last_name,email)')
    .eq('event_id', eventId);
  const rows = Array.isArray(data)
    ? (data as any[]).map((row) => ({
        ...(row as any),
        profile: unwrapProfile((row as any).profile),
      }))
    : [];
  return { data: rows as ChatMentionStatusRow[], error: error ?? null };
}

export function subscribeChatMentionStatuses(params: {
  eventId: string;
  supabase?: SupabaseClient;
  onChange: (payload: any) => void;
}): () => void {
  const supabase = params.supabase ?? getSupabaseClient();
  const channel = supabase
    .channel(`quote-chat-mention-status:${params.eventId}`)
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'quote_chat_mentions', filter: `event_id=eq.${params.eventId}` },
      (payload) => {
        recordRealtimeEvent({ source: 'order-chat-mentions', table: 'quote_chat_mentions', eventType: payload.eventType });
        params.onChange(payload);
      },
    )
    .subscribe();
  return () => {
    try {
      supabase.removeChannel(channel);
    } catch {
      /* noop */
    }
  };
}

export async function fetchChatReadReceipts(
  eventId: string,
  options?: ClientOption,
): Promise<{ data: ChatReadReceiptRow[]; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase
    .from('quote_chat_message_reads')
    .select('id,message_id,reader_profile_id,read_at,reader:reader_profile_id(id,full_name,first_name,last_name,email)')
    .eq('event_id', eventId);
  const rows = Array.isArray(data)
    ? (data as any[]).map((row) => ({
        ...(row as any),
        reader: unwrapProfile((row as any).reader),
      }))
    : [];
  return { data: rows as ChatReadReceiptRow[], error: error ?? null };
}

export function subscribeChatReadReceipts(params: {
  eventId: string;
  supabase?: SupabaseClient;
  onChange: (payload: any) => void;
}): () => void {
  const supabase = params.supabase ?? getSupabaseClient();
  const channel = supabase
    .channel(`quote-chat-reads:${params.eventId}`)
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'quote_chat_message_reads', filter: `event_id=eq.${params.eventId}` },
      (payload) => {
        recordRealtimeEvent({ source: 'order-chat-reads', table: 'quote_chat_message_reads', eventType: payload.eventType });
        params.onChange(payload);
      },
    )
    .subscribe();
  return () => {
    try {
      supabase.removeChannel(channel);
    } catch {
      /* noop */
    }
  };
}

export async function searchMentionCandidates(
  term: string,
  options?: ClientOption,
): Promise<{ data: ChatProfile[]; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const like = `%${term}%`;
  const { data, error } = await supabase
    .from('user_profiles')
    .select('id,full_name,first_name,last_name,email')
    .eq('is_archived', false)
    .or(`full_name.ilike.${like},first_name.ilike.${like},last_name.ilike.${like},email.ilike.${like}`)
    .order('full_name', { ascending: true })
    .limit(7);
  return { data: (data as ChatProfile[]) || [], error: error ?? null };
}

export async function sendChatMessage(
  payload: {
    eventId: string;
    body: string;
    attachments: any[];
    mentions: Array<{ profile_id: string; display_name: string }>;
    authorDisplayName: string | null;
    userId: string | null;
  },
  options?: ClientOption,
): Promise<PostgrestError | null> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { error } = await supabase.rpc('send_quote_chat_message', {
    p_event_id: payload.eventId,
    p_body: payload.body,
    p_mentions: payload.mentions,
    p_attachments: payload.attachments,
    p_author_display_name: payload.authorDisplayName,
    p_user_id: payload.userId,
  });
  return error ?? null;
}

export async function uploadChatAttachment(
  params: { bucket: string; path: string; file: File },
  options?: ClientOption,
): Promise<StorageError | null> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { error } = await supabase.storage.from(params.bucket).upload(params.path, params.file, { cacheControl: '1800', upsert: false });
  return error ?? null;
}

export async function createChatSignedUrl(
  params: { bucket: string; path: string; expiresIn: number },
  options?: ClientOption,
): Promise<{ url: string | null; error: StorageError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase.storage.from(params.bucket).createSignedUrl(params.path, params.expiresIn);
  return { url: data?.signedUrl ?? null, error: error ?? null };
}

export async function fetchChatProfile(
  profileId: string,
  options?: ClientOption,
): Promise<{ data: ChatProfile | null; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase
    .from('user_profiles')
    .select('id,full_name,first_name,last_name,email,headshot_url')
    .eq('id', profileId)
    .maybeSingle();
  return { data: (data as ChatProfile | null) ?? null, error: error ?? null };
}

export async function fetchUnreadMentions(
  profileId: string,
  options?: ClientOption,
): Promise<{ data: ChatMentionRow[]; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase
    .from('quote_chat_mentions')
    .select(
      `
        id,message_id,event_id,created_at,read_at,dismissed_at,
        message:quote_chat_messages(
          id,body,attachments,mentions,created_at,author_profile_id,author_display_name,
          author:author_profile_id(id,full_name,first_name,last_name,email)
        ),
        event:events(id,name,event_code)
      `,
    )
    .eq('mentioned_profile_id', profileId)
    // Direct and group chat mentions have no event; the mention popup links to an event's order.
    .not('event_id', 'is', null)
    .is('read_at', null)
    .is('dismissed_at', null)
    .order('created_at', { ascending: true })
    .limit(12);

  const rows = Array.isArray(data) ? data.map((row) => normaliseMentionRow(row)) : [];
  return { data: rows, error: error ?? null };
}

export function subscribeUnreadMentions(params: {
  profileId: string;
  supabase?: SupabaseClient;
  onInsert: (id: string) => void;
  onResolve: (id: string) => void;
}): () => void {
  const supabase = params.supabase ?? getSupabaseClient();
  const channel = supabase
    .channel(`quote-chat-mentions:${params.profileId}`)
    .on(
      'postgres_changes',
      { event: 'INSERT', schema: 'public', table: 'quote_chat_mentions', filter: `mentioned_profile_id=eq.${params.profileId}` },
      (payload) => {
        recordRealtimeEvent({ source: 'unread-mentions', table: 'quote_chat_mentions', eventType: payload.eventType });
        const id = (payload.new as any)?.id;
        if (id) params.onInsert(String(id));
      },
    )
    .on(
      'postgres_changes',
      { event: 'UPDATE', schema: 'public', table: 'quote_chat_mentions', filter: `mentioned_profile_id=eq.${params.profileId}` },
      (payload) => {
        recordRealtimeEvent({ source: 'unread-mentions', table: 'quote_chat_mentions', eventType: payload.eventType });
        const id = (payload.new as any)?.id;
        const readAt = (payload.new as any)?.read_at;
        const dismissedAt = (payload.new as any)?.dismissed_at;
        if (id && (readAt || dismissedAt)) {
          params.onResolve(String(id));
        }
      },
    )
    .subscribe();

  return () => {
    try {
      supabase.removeChannel(channel);
    } catch {
      /* noop */
    }
  };
}

export async function fetchMentionById(id: string, options?: ClientOption): Promise<ChatMentionRow | null> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase
    .from('quote_chat_mentions')
    .select(
      `
        id,message_id,event_id,created_at,read_at,dismissed_at,
        message:quote_chat_messages(
          id,body,attachments,mentions,created_at,author_profile_id,
          author_display_name,
          author:author_profile_id(id,full_name,first_name,last_name,email)
        ),
        event:events(id,name,event_code)
      `,
    )
    .eq('id', id)
    .not('event_id', 'is', null)
    .maybeSingle();
  if (error || !data) return null;
  return normaliseMentionRow(data);
}

export async function updateMentionStatus(
  id: string,
  updates: { read_at?: string | null; dismissed_at?: string | null },
  options?: ClientOption,
): Promise<PostgrestError | null> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { error } = await supabase.from('quote_chat_mentions').update(updates).eq('id', id);
  return error ?? null;
}

export type OrderChatSummary = {
  orderRef: string | null;
  eventName: string | null;
  eventCode: string | null;
};

export const orderChatSummaryQueryKey = (eventId: string | null | undefined) => ['order-chat-summary', eventId ?? ''];

export const getOrderChatSummaryQueryOptions = ({
  eventId,
  supabase = getSupabaseClient(),
  enabled = true,
}: {
  eventId: string | null | undefined;
  supabase?: SupabaseClient;
  enabled?: boolean;
}) => ({
  queryKey: orderChatSummaryQueryKey(eventId),
  enabled: enabled && Boolean(eventId),
  queryFn: async (): Promise<OrderChatSummary> => {
    if (!eventId) return { orderRef: null, eventName: null, eventCode: null };
    // Read everything from events — the orders table is deprecated.
    const eventRes = await supabase
      .from('events')
      .select('name,event_code')
      .eq('id', eventId)
      .maybeSingle();
    if (eventRes.error) throw eventRes.error;
    return {
      orderRef: (eventRes.data as any)?.event_code ? String((eventRes.data as any).event_code) : null,
      eventName: (eventRes.data as any)?.name ? String((eventRes.data as any).name) : null,
      eventCode: (eventRes.data as any)?.event_code ? String((eventRes.data as any).event_code) : null,
    };
  },
});

export async function sendQuickChatMessage(
  payload: { eventId: string; body: string; mentions: Array<{ profile_id: string; display_name: string }> },
  options?: ClientOption,
): Promise<PostgrestError | null> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { error } = await supabase.rpc('send_quote_chat_message', {
    _event_id: payload.eventId,
    _body: payload.body,
    _attachments: [],
    _mentions: payload.mentions,
  });
  return error ?? null;
}
